"use client";
import { useEffect, useRef, type ReactNode } from "react";
import { useSession } from "../auth-client";
import {
  clearAnalyticsIdentity,
  identifyUser,
  initAnalytics,
  setAnalyticsIdentity,
  track,
} from "../analytics";
import { consumePendingSignInMethod } from "../pending-signin";
import {
  consumePendingSignupIntent,
  clearPendingSignupIntent,
  clearSignupCallbackParams,
  isValidSignupIntent,
  matchesPendingSignupIntent,
} from "../pending-signup";
import { useAnalyticsContext } from "../hooks/use-analytics-context";

export function PostHogProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending: isSessionPending } = useSession();
  const context = useAnalyticsContext(session?.user?.id);
  const identifiedUserRef = useRef<string | null>(null);
  useEffect(() => {
    initAnalytics(process.env.NEXT_PUBLIC_POSTHOG_KEY);
  }, []);
  useEffect(() => {
    const userId = session?.user?.id;
    if (isSessionPending) return;
    if (
      userId &&
      identifiedUserRef.current &&
      identifiedUserRef.current !== userId
    ) {
      clearAnalyticsIdentity();
    }
    if (userId) {
      identifiedUserRef.current = userId;
      identifyUser(userId);
    } else {
      identifiedUserRef.current = null;
    }
    if (userId && !isSessionPending && context.data)
      setAnalyticsIdentity(userId, context.data);
    else if (!userId) clearAnalyticsIdentity();
  }, [context.data, isSessionPending, session?.user?.id]);
  // The Google half of `signed_in`: signIn.social() redirects away before an
  // event fired there could flush, so the sign-in page leaves a one-shot
  // sessionStorage flag and we emit here on the OAuth return -- but only once
  // the session has actually resolved to a user. Consuming clears the flag,
  // so a refresh of the landed page does not re-emit.
  useEffect(() => {
    if (!session?.user?.id) return;
    const method = consumePendingSignInMethod();
    if (method) track("signed_in", { method });
  }, [session?.user?.id]);
  useEffect(() => {
    let params: URLSearchParams;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }
    const marker = params.get("bb_signup");
    const nonce = params.get("intent");
    const validCallback = marker === "new" && isValidSignupIntent(nonce);
    if (validCallback && isSessionPending && matchesPendingSignupIntent(nonce))
      return;
    if (
      validCallback &&
      session?.user?.id &&
      consumePendingSignupIntent(nonce)
    ) {
      clearSignupCallbackParams();
      track("signed_up", { method: "google" });
    } else {
      clearSignupCallbackParams();
      clearPendingSignupIntent();
    }
  }, [isSessionPending, session?.user?.id]);
  return <>{children}</>;
}
